import { type IRowNode, type GridApi } from 'ag-grid-community';
import { type GridDataProcessorResult, type ExtraOptions, type TableActionsResult } from '../../types';
import { type ExportData } from 'common/visualizations/views/agGridReact/types';
import { type Row } from '../type';
import {
  DEFAULT_ROW_HEIGHT,
  NO_VALUE,
  NULL_GROUPING,
} from 'common/visualizations/views/agGridReact/Constants';
import { getForgeThemeColorConfig } from '../GridDataProcessor';
import { processConditionalCellStyles } from '../processConditionalCellStyles';
import { formatRows } from '../gridDataFormatter';
import { buildPivotTableColumns } from './commonHelpers';

export const processPivotFromGridState = (
  gridApi: GridApi,
  exportData: ExportData,
  extraOptions: ExtraOptions,
  tableActionsResult: TableActionsResult,
): GridDataProcessorResult => {
  // If we are pivoting, return with pivoted table data
  const pivotRowData = getPivotedRows(gridApi, extraOptions);
  const pivotTableColumns = buildPivotTableColumns(gridApi, exportData, extraOptions);
  if (extraOptions.showTotal && gridApi.getPinnedBottomRowCount() > 0) {
    const pinnedRow = gridApi.getPinnedBottomRow(0);
    if (pinnedRow?.data) {
      pivotRowData.push({ ...pinnedRow.data, _rowType: 'total' });
    }
  }
  const formattedRowData = formatRows({
    rowData: processConditionalCellStyles(
      pivotRowData,
      pivotTableColumns,
      extraOptions.columnFormats,
      exportData.columns,
      extraOptions.showSubTotal,
      extraOptions.groupExpansion,
    ),
    tableColumns: pivotTableColumns,
    viewColumns: exportData.columns,
    extraOptions,
    gridApi,
  });
  return {
    rowData: formattedRowData,
    tableColumns: pivotTableColumns,
    tableActionsResult,
    tableDef: {
      rowHeight: Number(gridApi.getGridOption('rowHeight')) || DEFAULT_ROW_HEIGHT,
    },
    processedRowCount: exportData.rows.length,
    forgeThemeColorConfig: getForgeThemeColorConfig(),
  };
};

/**
 * For a pivoted table we show what is displayed in the grid. We do not automatically expand all
 * groups. So if a user has expanded some groups and not others, only show rows in the ones that
 * are expanded. Skip over any rows inside groups that are not expanded.
 *
 */
export const getPivotedRows = (gridApi: GridApi, extraOptions: ExtraOptions): Row[] => {
  const rowData: Row[] = [];
  let previousNode: IRowNode | null = null;
  const groupedColumns = gridApi.getRowGroupColumns();

  gridApi.forEachNode((node) => {
    if (node?.parent?.expanded === false) {
      return;
    }

    if (previousNode && extraOptions.showSubTotal) {
      const previousLevel = previousNode.level;
      const currentLevel = node.level;

      // If we're moving up levels, add subtotal rows
      if (currentLevel < previousLevel) {
        addSubtotalRows(rowData, previousNode, currentLevel);
      }
    }

    if (node.data) {
      const maxLevel = (groupedColumns?.length ?? 0) - 1;
      if (node.group && node.level < maxLevel && node.field && extraOptions.showSubTotal) {
        // For group headers that aren't at the lowest level, only include the group field
        rowData.push({
          [node.field]: node.key === NULL_GROUPING ? NO_VALUE : node.key,
        });
      } else {
        // For leaf nodes and lowest-level groups, include all data
        if (node.key == NULL_GROUPING && node.field) {
          node.data = {
            ...node.data,
            [node.field]: NO_VALUE,
          };
        }
        rowData.push(node.data);
      }
    }

    previousNode = node;
  });

  if (previousNode && extraOptions.showSubTotal) {
    const nodeLevel = (previousNode as IRowNode).level;
    if (typeof nodeLevel === 'number') {
      // Always include all subtotals up to the root
      addSubtotalRows(rowData, previousNode, -1);
    }
  }

  return rowData;
};

/**
 * Helper method to add subtotal rows when moving up levels in the grid hierarchy
 * This optimized version efficiently finds and adds parent nodes
 */
export const addSubtotalRows = (rowData: Row[], previousNode: IRowNode | null, toLevel: number): void => {
  if (!previousNode) {
    return;
  }

  // Start with the node at the current level
  let groupNode = previousNode;

  // Track parent nodes we've already found to avoid duplicates
  const processedLevels = new Set<number>();

  // Continue until we reach the target level or run out of parents
  while (groupNode && groupNode.level > toLevel) {
    // Get the parent node (one level up)
    const parentNode = groupNode.parent;

    // If we found a valid parent and haven't processed this level yet
    if (
      parentNode &&
      parentNode.group &&
      parentNode.data &&
      parentNode.level >= toLevel &&
      !processedLevels.has(parentNode.level)
    ) {
      // Add subtotal for this parent level
      rowData.push({
        _rowType: 'subTotal',
        _groupLevel: parentNode.level,
        ...parentNode.data,
      });

      // Mark this level as processed
      processedLevels.add(parentNode.level);
    }

    // Move up to the next level
    if (parentNode) {
      groupNode = parentNode;
    } else {
      break; // Exit loop if we've reached the top of the hierarchy
    }
  }
};
